import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * FileItem component for displaying a file in the briefcase
 * @param {Object} file - The file object to display
 * @param {Function} onNavigateToMessage - Function to navigate to the message
 * @param {Function} onReferenceInChat - Function to reference the file in chat
 * @param {Function} onRename - Function to rename the file
 * @param {Function} onTogglePin - Function to toggle pin status
 */
const FileItem = ({ 
  file, 
  onNavigateToMessage, 
  onReferenceInChat,
  onRename,
  onTogglePin,
  showAction = true // Default to true for backward compatibility
}) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const contextMenuRef = useRef(null);
  const renameInputRef = useRef(null);
  const buttonRef = useRef(null);

  // Submit rename - defined early to avoid reference errors
  const submitRename = useCallback(() => {
    if (newName.trim() && newName !== file.name) {
      onRename(file.id, newName.trim());
    }
    setIsRenaming(false);
  }, [file.id, file.name, newName, onRename]);

  // Close context menu when clicking outside, scrolling, or resizing
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showContextMenu && contextMenuRef.current && !contextMenuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowContextMenu(false);
      }
    };

    const handleScrollOrResize = () => {
      if (showContextMenu) {
        setShowContextMenu(false);
      }
    };
    
    if (showContextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      
      // Add listener to briefcase content
      const briefcaseContent = document.querySelector('.briefcase-content');
      if (briefcaseContent) {
        briefcaseContent.addEventListener('scroll', handleScrollOrResize, true);
      }
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
        
        if (briefcaseContent) {
          briefcaseContent.removeEventListener('scroll', handleScrollOrResize, true);
        }
      };
    }
  }, [showContextMenu]);

  // Focus rename input when entering rename mode
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  // Handle clicks outside the rename input
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isRenaming && renameInputRef.current && !renameInputRef.current.contains(event.target)) {
        submitRename();
      }
    };

    if (isRenaming) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isRenaming, submitRename]);

  // Get file icon based on type
  const getFileIcon = () => {
    if (file.type === 'uploaded') {
      return 'description';
    } else if (file.type === 'generated') {
      return 'data_object';
    }
    return 'insert_drive_file';
  };

  // Format timestamp to show date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Format time
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Check if date is today, yesterday, or earlier
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${timeStr}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${timeStr}`;
    } else {
      // Format date as MM/DD/YY
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear().toString().substr(-2);
      return `${month}/${day}/${year}, ${timeStr}`;
    }
  };

  // Handle click on file details
  const handleFileDetailsClick = (e) => {
    if (e.detail === 2) {
      startRenaming();
    } else {
      onNavigateToMessage(file.messageId);
    }
  };

  // Start renaming the file
  const startRenaming = () => {
    setNewName(file.name);
    setIsRenaming(true);
    setShowContextMenu(false);
  };

  // Handle rename input key press
  const handleRenameKeyPress = (e) => {
    if (e.key === 'Enter') {
      submitRename();
    } else if (e.key === 'Escape') {
      setIsRenaming(false);
    }
  };

  // Create a portal for the context menu
  const renderContextMenu = () => {
    if (!showContextMenu || !buttonRef.current) return null;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 180;
    const menuHeight = 125; // Approximate height
    
    // Fix positioning calculation
    let top = buttonRect.bottom;
    let left = buttonRect.right - menuWidth;
    
    // Adjust if menu would go off screen
    if (top + menuHeight > window.innerHeight) {
      top = buttonRect.top - menuHeight;
    }
    
    if (left < 0) {
      left = buttonRect.left;
    }
    
    // Don't add window.scrollX/Y - fixed positioning already accounts for scroll
    
    return ReactDOM.createPortal(
      <div 
        ref={contextMenuRef}
        className="context-menu" 
        style={{ 
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          zIndex: 99999
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling
      >
        {file.type === 'generated' && (
          <div 
            className="context-menu-item" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onReferenceInChat(file);
              setShowContextMenu(false);
            }}
          >
            <span className="material-symbols-outlined">link</span>
            <span>Reference in chat</span>
          </div>
        )}
        <div 
          className="context-menu-item" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNavigateToMessage(file.messageId);
            setShowContextMenu(false);
          }}
        >
          <span className="material-symbols-outlined">location_searching</span>
          <span>Go to message</span>
        </div>
        <div 
          className="context-menu-item" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startRenaming();
          }}
        >
          <span className="material-symbols-outlined">edit</span>
          <span>Rename</span>
        </div>
        <div 
          className="context-menu-item" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTogglePin(file.id);
            setShowContextMenu(false);
          }}
        >
          <span className="material-symbols-outlined">{file.pinned ? 'unpublished' : 'push_pin'}</span>
          <span>{file.pinned ? 'Unpin' : 'Pin'}</span>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div 
      className={`file-item ${file.pinned ? 'pinned' : ''} ${file.type === 'generated' ? 'generated-content' : ''} ${file.type === 'uploaded' ? 'uploaded-content' : ''}`}
    >
      <div className="file-icon">
        <span className="material-symbols-outlined">
          {getFileIcon()}
        </span>
        {file.pinned && (
          <span className="pin-indicator material-symbols-outlined">
            push_pin
          </span>
        )}
        {file.type === 'generated' && file.status === 'approved' && (
          <span className="approve-indicator material-symbols-outlined">
            verified
          </span>
        )}
        {file.type === 'generated' && file.status === 'rejected' && (
          <span className="reject-indicator material-symbols-outlined">
            cancel
          </span>
        )}
      </div>
      <div className="file-details" onClick={handleFileDetailsClick}>
        {isRenaming ? (
          <input
            ref={renameInputRef}
            type="text"
            className="rename-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={submitRename}
            onKeyDown={handleRenameKeyPress}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <div className="file-name" title={file.name}>
              {file.name}
            </div>
            {showAction && file.type === 'generated' && file.action && (
              <div className="file-action-tag" title={`Action: ${file.action}`}>
                {file.action}
              </div>
            )}
            <div className="file-info">
              {formatDateTime(file.timestamp)}
            </div>
          </>
        )}
      </div>
      <div className="file-actions">
        <button 
          ref={buttonRef}
          className="file-action-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowContextMenu(!showContextMenu);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowContextMenu(true);
          }}
          title="More actions"
        >
          <span className="material-symbols-outlined">more_vert</span>
        </button>
      </div>

      {renderContextMenu()}
    </div>
  );
};

export default FileItem;
